import { SERVICES_FEATURES } from '@spa/shared/constants/services-features';
import { PROPERTY_RENT_TYPE } from '@spa/shared/constants/rentType';
import { PURPOSE_OF_STAY_TYPES } from '@spa/shared/constants/purposeOfStay';
import { GENDER_RESTRICTIONS } from '@spa/shared/constants/genderRestrictions';
import { getSearchQueryMetadata } from '@spa/shared/utils/searchQuery';
import { capitalizeFirstLetter, joinTranslations } from '@spa/shared/utils/stringManipulation';

export const describeFilters = ({ t, language, searchQuery }) => {
  const {
    studentResidence,
    rentTypes,
    houseFeatures,
    petsAllowed,
    occupationRestrictions,
    genderRestriction
  } = getFiltersTranslations(searchQuery);
  const { isGenderExclusive, isSortingByAscendingPrices } = getSearchQueryMetadata(searchQuery);
  const dynamicTranslations = DYNAMIC_TRANSLATIONS_OBJECT[language];

  const rentalTypes = [...(studentResidence ? [studentResidence] : []), ...rentTypes];
  const rentalTypesText = rentalTypes.length
    ? joinTranslations(
        t,
        rentalTypes.map(({ key }) => capitalizeFirstLetter(t(key))),
        t('and')
      )
    : t('Rooms, Studios & Apartments');

  const featuresAndPetAllowed = [...houseFeatures, ...(petsAllowed ? [petsAllowed] : [])];
  const featuresAndPetsAllowedText = featuresAndPetAllowed.length
    ? featuresAndPetAllowed.map(({ key }) => capitalizeFirstLetter(t(key))).join(', ')
    : '';

  const suitableFor = [
    ...occupationRestrictions,
    ...(!isGenderExclusive && genderRestriction ? [genderRestriction] : [])
  ];
  const suitableForText = suitableFor?.length
    ? joinTranslations(
        t,
        suitableFor.map(({ key }) => t(key)),
        t('and'),
        t('Suitable for')
      )
    : '';

  const genderExclusivityText = isGenderExclusive ? t(genderRestriction.key) : '';

  const pricingSortText =
    isSortingByAscendingPrices && !!dynamicTranslations ? dynamicTranslations.cheap({ searchQuery }) : '';

  return {
    rentalTypesText,
    featuresAndPetsAllowedText,
    suitableForText,
    genderExclusivityText,
    pricingSortText
  };
};

const getFiltersTranslations = searchQuery => {
  const { hasStudentResidence, hasPetsAllowed } = getSearchQueryMetadata(searchQuery);

  const studentResidence = hasStudentResidence ? { key: 'Student Residence' } : null;
  const rentTypes = getRentTypesTranslations(searchQuery['rent-type']);

  const houseFeatures = getHouseFeaturesTranslations(searchQuery['property-features']);
  const petsAllowed = hasPetsAllowed ? { key: 'Pets Allowed' } : null;

  const occupationRestrictions = getOccupationRestrictionsTranslations(searchQuery['restrictions-occupation']);
  const genderRestriction = getGenderRestrictionTranslation(searchQuery['restrictions-gender']);

  return { studentResidence, rentTypes, houseFeatures, petsAllowed, occupationRestrictions, genderRestriction };
};

const getRentTypesTranslations = rentTypeFilters => {
  if (!rentTypeFilters) {
    return [];
  }

  return rentTypeFilters.reduce((translations, rentType) => {
    if (rentType === PROPERTY_RENT_TYPE.UNIT) {
      return [...translations, { key: 'Private Room' }];
    }

    if (rentType === PROPERTY_RENT_TYPE.SUBUNIT) {
      return [...translations, { key: 'Shared Room' }];
    }

    if (rentType === PROPERTY_RENT_TYPE.PROPERTY) {
      return [...translations, { key: 'Apartment' }];
    }

    return translations;
  }, []);
};

const getHouseFeaturesTranslations = (houseFeatures = []) => {
  if (!houseFeatures) {
    return [];
  }

  return houseFeatures.map(filter => ({ key: SERVICES_FEATURES[filter]?.translation || filter }));
};

const getOccupationRestrictionsTranslations = occupationRestrictions => {
  if (!occupationRestrictions) {
    return [];
  }

  const translations = occupationRestrictions.reduce((translations, occupation) => {
    if (occupation === PURPOSE_OF_STAY_TYPES.PROFESSIONAL_PLACEMENT) {
      return [...translations, { key: 'Professionals' }];
    }

    if ([PURPOSE_OF_STAY_TYPES.UNDERGRADUATE, PURPOSE_OF_STAY_TYPES.POSTGRADUATE].includes(occupation)) {
      return [...translations, { key: 'Students' }];
    }

    return translations;
  }, []);

  return eliminateDuplicateTranslations(translations);
};

const getGenderRestrictionTranslation = genderRestriction => {
  const KEY_GENDER_RESTRICTIONS = {
    [GENDER_RESTRICTIONS.FEMALE_ROOM]: { key: 'Females' },
    [GENDER_RESTRICTIONS.FEMALE_PROPERTY]: { key: 'Female-Only' },
    [GENDER_RESTRICTIONS.MALE_ROOM]: { key: 'Males' },
    [GENDER_RESTRICTIONS.MALE_PROPERTY]: { key: 'Male-Only' }
  };

  return KEY_GENDER_RESTRICTIONS[genderRestriction] ?? null;
};

const eliminateDuplicateTranslations = translations =>
  translations.reduce((uniqueTranslations, translation) => {
    if (!uniqueTranslations.some(({ key }) => key === translation.key)) {
      return [...uniqueTranslations, translation];
    }

    return uniqueTranslations;
  }, []);

const DYNAMIC_TRANSLATIONS_OBJECT = {
  de: {
    cheap: ({ searchQuery }) => {
      const { hasStudentResidence, hasBedroom, hasBed, rentTypesAndStudentResidenceCount } = getSearchQueryMetadata(
        searchQuery
      );

      const isNeutralSingular =
        rentTypesAndStudentResidenceCount === 1 && (hasStudentResidence || hasBedroom || hasBed);

      return isNeutralSingular ? 'Günstiges' : 'Günstige';
    }
  },
  en: {
    cheap: () => {
      return 'Cheap';
    }
  },
  es: {
    cheap: ({ searchQuery }) => {
      const {
        rentTypesAndStudentResidenceCount,
        hasEntireProperty,
        hasStudentResidence,
        hasBedroom,
        hasBed
      } = getSearchQueryMetadata(searchQuery);

      const isMasculineSingular = rentTypesAndStudentResidenceCount === 1 && hasEntireProperty;
      if (isMasculineSingular) {
        return 'barato';
      }

      const isFeminineSingular =
        rentTypesAndStudentResidenceCount === 1 && (hasStudentResidence || hasBedroom || hasBed);
      if (isFeminineSingular) {
        return 'barata';
      }

      const isFemininePlural = rentTypesAndStudentResidenceCount > 1 && !hasEntireProperty;
      if (isFemininePlural) {
        return 'baratas';
      }

      return 'baratos';
    }
  },
  fr: {
    cheap: ({ searchQuery }) => {
      const {
        rentTypesAndStudentResidenceCount,
        hasEntireProperty,
        hasStudentResidence,
        hasBedroom,
        hasBed
      } = getSearchQueryMetadata(searchQuery);

      const isMasculineSingular = rentTypesAndStudentResidenceCount === 1 && hasEntireProperty;
      if (isMasculineSingular) {
        return 'pas cher';
      }

      const isFeminineSingular =
        rentTypesAndStudentResidenceCount === 1 && (hasStudentResidence || hasBedroom || hasBed);
      if (isFeminineSingular) {
        return 'pas chère';
      }

      return 'bon marché';
    }
  },
  it: {
    cheap: ({ searchQuery }) => {
      const {
        rentTypesAndStudentResidenceCount,
        hasEntireProperty,
        hasStudentResidence,
        hasBedroom,
        hasBed
      } = getSearchQueryMetadata(searchQuery);

      const isMasculineSingular = rentTypesAndStudentResidenceCount === 1 && hasEntireProperty;
      if (isMasculineSingular) {
        return 'economico';
      }

      const isFeminineSingular =
        rentTypesAndStudentResidenceCount === 1 && (hasStudentResidence || hasBedroom || hasBed);
      if (isFeminineSingular) {
        return 'economica';
      }

      const isFemininePlural = rentTypesAndStudentResidenceCount > 1 && !hasEntireProperty;
      if (isFemininePlural) {
        return 'economicas';
      }

      return 'economicos';
    }
  },
  nl: {
    cheap: ({ searchQuery }) => {
      const { rentTypesAndStudentResidenceCount } = getSearchQueryMetadata(searchQuery);

      const isIndefiniteSingular = rentTypesAndStudentResidenceCount === 1;

      return isIndefiniteSingular ? 'Goedkoop' : 'Goedkope';
    }
  },
  pl: {
    cheap: ({ searchQuery }) => {
      const { rentTypesAndStudentResidenceCount, hasStudentResidence, hasBedroom, hasBed } = getSearchQueryMetadata(
        searchQuery
      );

      const isMasculineSingular =
        rentTypesAndStudentResidenceCount === 1 && (hasStudentResidence || hasBedroom || hasBed);

      return isMasculineSingular ? 'Tani' : 'Tanie';
    }
  },
  pt: {
    cheap: ({ searchQuery }) => {
      const {
        rentTypesAndStudentResidenceCount,
        hasStudentResidence,
        hasEntireProperty,
        hasBedroom,
        hasBed
      } = getSearchQueryMetadata(searchQuery);

      const isFeminineSingular = rentTypesAndStudentResidenceCount === 1 && hasStudentResidence;
      if (isFeminineSingular) {
        return 'barata';
      }

      const isMasculineSingular =
        rentTypesAndStudentResidenceCount === 1 && (hasEntireProperty || hasBedroom || hasBed);
      if (isMasculineSingular) {
        return 'barato';
      }

      return 'baratos';
    }
  },
  zh: {
    cheap: () => {
      return '便宜的';
    }
  }
};
